import { faPen } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Divider, Typography, useTheme } from '@mui/material';
import { noop } from 'lodash';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import CbButton from '../../../../../../components/Buttons/CbButton';
import { Flexbox } from '../../../../../../components/layout/Flexbox';
import Showable from '../../../../../../components/Showable';
import type { WithChildrenProps } from '../../../../../../types/components/common';
import { FirmoFormContext } from './FirmoFormProvider';

// Context for controlling edit mode from parent components
type ReadWriteFormStateContextValue = {
  isEditMode: boolean;
  onEditStart: () => void;
  onEditEnd: () => void;
  hideActions?: boolean;
} | null;

const ReadWriteFormStateContext = React.createContext<ReadWriteFormStateContextValue>(null);

/**
 * Provider component that allows parent components to control edit mode and action visibility
 * If provided, child components will use this edit mode instead of local state
 */
export const ReadWriteFormStateProvider: React.FC<{
  children: React.ReactNode;
  isEditMode?: boolean;
  onEditStart?: () => void;
  onEditEnd?: () => void;
  /**
   * Optional: If true, hides the save/cancel buttons in ReadWriteFormActions.
   * Useful for one-shot views where actions are handled at a higher level.
   */
  hideActions?: boolean;
}> = ({ children, isEditMode, onEditStart, onEditEnd, hideActions }) => {
  // If isEditMode is provided, use it; otherwise, provide null to use default behavior
  const contextValue: ReadWriteFormStateContextValue =
    isEditMode !== undefined
      ? {
          isEditMode,
          onEditStart: onEditStart || noop,
          onEditEnd: onEditEnd || noop,
          hideActions,
        }
      : null;

  return (
    <ReadWriteFormStateContext.Provider value={contextValue}>
      {children}
    </ReadWriteFormStateContext.Provider>
  );
};

export const useReadWriteFormState = () => {
  // ALWAYS call hooks first, regardless of context (Rules of Hooks requirement)
  // This ensures hooks are called in the same order on every render
  const [isEditMode, setEditMode] = React.useState(false);

  // Then check context
  const contextValue = React.useContext(ReadWriteFormStateContext);

  // If context is provided, use it (local state is unused but that's fine)
  if (contextValue !== null) {
    return contextValue;
  }

  // Otherwise, fall back to local state (default behavior)
  const onEditStart = () => setEditMode(true);
  const onEditEnd = () => setEditMode(false);

  return { isEditMode, onEditStart, onEditEnd };
};

interface SectionHeaderProps extends WithChildrenProps {
  onClick: () => void;
  isEditMode?: boolean;
  editIconId?: string;
}

export function FormSectionHeader({
  children,
  onClick,
  isEditMode,
  editIconId,
}: SectionHeaderProps) {
  const theme = useTheme();
  const { isEditable } = React.useContext(FirmoFormContext);
  return (
    <>
      <Flexbox>
        <Typography
          style={{
            fontSize: '1.6rem',
            textTransform: 'uppercase',
            color: theme.palette.text.secondary,
          }}
        >
          {children}
        </Typography>
        <Showable show={!isEditMode && isEditable}>
          <span
            id={editIconId}
            onClick={onClick}
            style={{ cursor: 'pointer', display: 'inline-flex' }}
            data-testid="edit-icon"
          >
            <FontAwesomeIcon
              icon={faPen}
              size="xl"
            />
          </span>
        </Showable>
      </Flexbox>
      <Divider />
    </>
  );
}

interface ReadWriteFormActionsBaseProps {
  isEditMode: boolean;
  onEditEnd: () => void;
  onCancel?: () => void;
}

export function ReadWriteFormActions({
  isEditMode,
  onEditEnd,
  onCancel,
}: ReadWriteFormActionsBaseProps) {
  const formContext = useFormContext();
  const contextValue = React.useContext(ReadWriteFormStateContext);

  // Check if actions should be hidden via context
  const shouldHideActions = contextValue?.hideActions === true;

  if (!isEditMode || shouldHideActions) {
    return null;
  }

  const handleCancel = () => {
    formContext?.reset?.();
    if (typeof onCancel === 'function') {
      onCancel();
    }
    onEditEnd();
  };

  return (
    <>
      <Divider />
      <Flexbox style={{ margin: '1rem 0' }} justifyContent="flex-end">
        <CbButton
          styleName="cancel"
          onClick={handleCancel}
          size="small"
          disabled={formContext.formState.isSubmitting}
        >
          Cancel
        </CbButton>
        <CbButton
          styleName="ctaButton"
          type="submit"
          size="small"
          loading={formContext.formState.isSubmitting}
          disabled={formContext.formState.isSubmitting}
        >
          Save
        </CbButton>
      </Flexbox>
    </>
  );
}
